import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Send, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

export const Messages = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [messages, setMessages] = useState([]);
  const [partner, setPartner] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/messages/conversations').then(r => { setConvos(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!userId) {
      setPartner(null); setMessages([]); return;
    }
    api.get(`/users/${userId}`).then(r => setPartner(r.data)).catch(() => {});
    api.get(`/messages/${userId}`).then(r => setMessages(r.data)).catch(() => {});
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !userId) return;
    try {
      const { data } = await api.post('/messages', { recipient_id: userId, text });
      setMessages(m => [...m, data]);
      setText('');
      // Refresh convos
      const r = await api.get('/messages/conversations');
      setConvos(r.data);
    } catch {}
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6"
      >
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Messages</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          Direct line
        </h1>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        {/* Convo list */}
        <div className="md:col-span-1 post-card p-3 overflow-y-auto">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] px-3 py-2">Inbox</div>
          {loading ? (
            <div className="animate-pulse h-32" />
          ) : convos.length === 0 ? (
            <div className="p-6 text-center text-[#A1A1AA] text-sm">
              <MessageSquare size={24} className="mx-auto mb-2 text-[#52525B]" />
              No conversations yet. Message someone from their profile.
            </div>
          ) : (
            convos.map(c => (
              <button
                key={c.user.user_id}
                onClick={() => navigate(`/messages/${c.user.user_id}`)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                  userId === c.user.user_id ? 'bg-[#FFB000]/10 border border-[#FFB000]/20' : 'hover:bg-white/5'
                }`}
                data-testid={`convo-${c.user.username}`}
              >
                <img
                  src={c.user.avatar_path ? fileUrl(c.user.avatar_path) : avatarFallback(c.user.name)}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-sm truncate font-heading">{c.user.name}</div>
                  <div className="text-[#A1A1AA] text-xs truncate">{c.last_message.text}</div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div className="md:col-span-2 post-card flex flex-col">
          {partner ? (
            <>
              <Link
                to={`/profile/${partner.user_id}`}
                className="flex items-center gap-3 p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
                data-testid="thread-header"
              >
                <img
                  src={partner.avatar_path ? fileUrl(partner.avatar_path) : avatarFallback(partner.name)}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-white font-bold font-heading">{partner.name}</div>
                  <div className="text-[#A1A1AA] text-xs">@{partner.username}</div>
                </div>
              </Link>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 && (
                  <div className="text-center text-[#A1A1AA] text-sm py-12">
                    No messages yet. Say hey.
                  </div>
                )}
                {messages.map(m => {
                  const isMe = m.sender_id === user?.user_id;
                  return (
                    <div
                      key={m.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${m.id}`}
                    >
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                        isMe ? 'bg-[#FFB000] text-black rounded-br-sm' : 'bg-[#1A1A1D] text-white rounded-bl-sm border border-white/5'
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="p-4 border-t border-white/5 flex gap-2">
                <input
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Type a message..."
                  className="input-default flex-1"
                  data-testid="message-input"
                />
                <button type="submit" className="btn-pill btn-primary w-12 h-12 flex items-center justify-center p-0" data-testid="message-send">
                  <Send size={18} />
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MessageSquare size={36} className="text-[#52525B] mb-3" />
              <h3 className="font-heading font-bold text-xl text-white mb-2">Select a conversation</h3>
              <p className="text-[#A1A1AA] text-sm">Or message someone from their profile to start.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Messages;
